import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import { MemberInfoValue } from '../../shared/component/headless/input/InputBody';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { BlackFitBtn } from '../../shared/component/headless/button/BtnBody';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useValidation } from '../../shared/hooks/useValidation';
import Space from '../../shared/Space';
import { useMember } from '../../shared/hooks/useApi/useMember';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import searchIcon from '../../asset/icon/see.png';
import searchNotIcon from '../../asset/icon/see_not.png';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import deleteIcon from '../../asset/icon/delete.png';
import { ChangeModalFrame } from '../../shared/component/modal/ChangeModalFrame';


interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const ChangePWModal: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { user } = useAuth();
  const { standardModal, isClosing, clearModal } = useModalStore();
  const { updatePassword } = useMember();
  const { 
    PASSWORD_REGEX,
    explainPasswordIssues
  } = useValidation();
  const [showPassword, setShowPassword] = useState(false);
  const [pw, setPw] = useState(standardModal?.value || "");
  const [newPw, setNewPw] = useState("");
  const [error, setError] = useState('');

  useEffect(() => {
    if (pw.length > 0 || newPw.length > 0 ) {
      setIsUserInput(true);
    } else {
      setIsUserInput(false);
    }
  }, [pw, newPw]);

  if (user == null || standardModal == null) return null;

  const handleSubmit = async () => {
    if (!validateNewPassword(newPw)) {
      return;
    }
    setError('');

    await updatePassword({
      id: user.id,
      password: pw,
      newPassword: newPw,
    });
  };

  const validateNewPassword = (value: string) => {
    if (value.length === 0) {
      setError('');
      return true;
    }
    if (!PASSWORD_REGEX.test(value)) {
      const issues = explainPasswordIssues(value);
      setError(issues[0] ?? 'Password needs a little adjustment.');
      return false;
    }
    setError('');
    return true;
  };

  const changeNewPasswordInput = (value: string) => {
    validateNewPassword(value);
    setNewPw(value);
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <ChangeModalFrame
      onClick={(e) => e.stopPropagation()}
      $isClosing={isClosing}
    >
      <ChangeModalComp>
        <HeaderRow>
          <TitleGroup>
            <Title>{getModalMessage('CHANGE_PASSWORD_TITLE')}</Title>
            <UniversalIconBtn
              size="fit"
              isTransparent={true}
              border={false}
              backgroundImg={showPassword ? searchIcon : searchNotIcon}
              onClick={togglePasswordVisibility}
            />
          </TitleGroup>
          <UniversalIconBtn
            size="small"
            border={false}
            isTransparent
            hoverOpacity
            backgroundImg={deleteIcon}
            onClick={() => clearModal()}
          />
        </HeaderRow>
        <HeadlessInput
          type={showPassword ? 'text' : 'password'}
          value={pw}
          handleChange={(e) => setPw(e.target.value)}
          placeholder={getModalMessage('ENTER_CURRENT_PASSWORD')}
          StyledInput={MemberInfoValue}
        />
        <HeadlessInput
          type={showPassword ? 'text' : 'password'}
          value={newPw}
          handleChange={(e) => changeNewPasswordInput(e.target.value)}
          placeholder={getModalMessage('CREATE_NEW_PASSWORD')}
          StyledInput={MemberInfoValue}
        />
        <Space $height="20px">
          {error && <ErrorText>{error}</ErrorText>}
        </Space>
        <BtnContainer>
          <HeadlessBtn
            value={getBtnName('CHANGE')}
            handleClick={handleSubmit}
            StyledBtn={BlackFitBtn}
          />
        </BtnContainer>
      </ChangeModalComp>
    </ChangeModalFrame>
  );
};

const ChangeModalComp = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: 17px;
  padding: 0.5vw;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-bottom: 3vh;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const Title = styled.h2`
  ${({ theme }) => theme.typography.Body_02_1};
`;

const BtnContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 1vw;
`

const ErrorText = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_alert_red};
  ${({ theme }) => theme.typography.Body_04};
`;

export default ChangePWModal;
